/**
 * The edition bundle's life on the device.
 *
 * **Stored first, network second.** A stored copy is handed to the screen AT
 * ONCE; the network is consulted afterwards, with the stored version as an
 * ETag, and a newer bundle replaces it when (if) it lands. On festival wifi an
 * unchanged bundle costs one 304 and no bytes — and a request that hangs costs
 * nothing at all, because nothing is waiting for it.
 *
 * With no stored copy there is nothing to show but the network, so it is
 * awaited — with a ceiling, because a hanging request must never mean an
 * endless skeleton.
 *
 * A new version is applied ATOMICALLY. A half-updated lineup on disk is worse
 * than a stale one — someone would read a set time that no longer exists
 * beside one that does, with nothing to tell them apart.
 */
import { db, type StoredBundle } from './db';
import { apiUrl } from '@/lib/tenant';
import { isFramed } from '@/lib/framed';
import * as apiModule from '@/lib/api';
import { NETWORK_TIMEOUT_MS, withTimeout } from './net';
import type { EditionBundle } from '@myrave/shared';
import { noteServerReached } from '@/lib/reach';
import { signedInHere } from '@/lib/signOut';
import { passHeaderSoon } from '@/lib/dataPass';
import { overForOffline } from './pastRaves';

export async function readStored(editionId: string): Promise<StoredBundle | undefined> {
  try {
    return await db.bundles.get(editionId);
  } catch {
    return undefined;
  }
}

/**
 * The stored copy, if it is the signed-in account's own (the user,
 * 2026-10-16: a new account on the same phone opened OZORA's live page from
 * the copy the last person stored, before being sent to "Attend OZORA").
 *
 * Who is signed in comes from the platform cookies every host shares
 * (`signedInHere`), which are read with no signal — so an account's own copy
 * still opens offline. **Signed out, nothing stored is used** (the user,
 * 2026-10-27: "if the user is not signed in it's only online mode"): the rave
 * comes from the network or not at all.
 */
export function ownStored(stored: StoredBundle | undefined): StoredBundle | undefined {
  const who = signedInHere();
  if (!stored || !who) return undefined;
  // A rave over by more than two days works online only (the user, 2026-11-02; `pastRaves.ts`).
  if (overForOffline(stored.bundle?.edition?.endsAt)) return undefined;
  return stored.owner === who ? stored : undefined;
}

/** Whose a downloaded copy is: the account this page is signed in as — none signed out, and then nothing is kept. */
function owner(): string | null {
  if (!signedInHere()) return null;
  return apiModule.currentAccountId?.() ?? signedInHere();
}

export interface RefreshResult {
  bundle: EditionBundle;
  /**
   * 'stored'    = read from this device, the network not asked yet (or still out)
   * 'fresh'     = downloaded just now
   * 'unchanged' = the server said 304: the stored copy is current
   * 'offline'   = the request FAILED or did not answer in 2.5s; the stored copy is shown
   */
  source: 'stored' | 'fresh' | 'unchanged' | 'offline';
  /**
   * When the copy being shown last came from the server, epoch ms. It is what
   * lets the schedule say "stored copy, synced at 03:42" rather than just
   * "offline", which leaves somebody unsure whether a set time is this
   * morning's or last Tuesday's.
   */
  fetchedAt?: number;
}

/**
 * The request could not reach the server AND there is no copy on this device.
 *
 * The one failure a festival app cannot paper over, and a different fact from
 * "the server answered with an error" — so it is its own type, and the screen
 * can say "get to a signal" rather than "try again later".
 */
export class NoStoredCopyError extends Error {
  constructor(cause: unknown) {
    super('no network and no stored copy of this lineup', { cause });
    this.name = 'NoStoredCopyError';
  }
}

/**
 * How long a FIRST load (nothing stored) may wait for the server to start
 * answering. Longer than the 2.5s race — a first bundle is the whole lineup
 * and there is nothing else to show — but finite.
 */
export const FIRST_LOAD_TIMEOUT_MS = 20_000;

/** The bearer header, when this build's api module has one (tests mock it). */
function bearer(): Record<string, string> {
  try {
    return apiModule.authHeader?.() ?? {};
  } catch {
    return {};
  }
}

async function refusal(res: Response): Promise<Error> {
  const body = (await res.json().catch(() => ({}))) as { error?: string; message?: string; details?: unknown };
  try {
    return new apiModule.ApiError(res.status, body.error ?? 'error', body.message ?? `bundle request failed: ${res.status}`, body.details, body);
  } catch {
    return new Error(`bundle request failed: ${res.status}`);
  }
}

function isApiError(err: unknown): boolean {
  try {
    return err instanceof apiModule.ApiError;
  } catch {
    return false;
  }
}

/**
 * Did the server ANSWER no — as opposed to not answering?
 *
 * Only a network failure, a hang, or a server that is itself broken (5xx,
 * 408, 429) is "offline" and may fall back on the stored copy. A 401, 402,
 * 403 or 404 is the server saying something about THIS person and THIS rave,
 * and it used to be papered over as 'offline' whenever a copy was stored — so
 * the paywall and the sign-in never showed, and the schedule's stored-copy
 * notice said "offline" over a perfectly good connection.
 */
export function isRefusal(err: unknown): boolean {
  if (!isApiError(err)) return false;
  const { status, code } = err as { status: number; code?: string };
  // The account's own gates (`CompleteAccount`, `RequireApproval`) own those
  // screens; the lineup already on this phone stays readable behind them.
  if (code === 'account_incomplete' || code === 'awaiting_approval') return false;
  return status >= 400 && status < 500 && status !== 408 && status !== 429;
}

/**
 * Editions whose last revalidation was REFUSED. Their next load asks the
 * network first instead of serving the stored copy, so the refusal reaches
 * the screen as the query's error; cleared by the next answer that is not a
 * refusal. Without it, a refetch served the stored copy, revalidated, was
 * refused again, and refetched — a loop.
 */
const refused = new Set<string>();

/** Test support. */
export function resetRefusalsForTests(): void {
  refused.clear();
}

/** Ask for a new access token; null when there is none to be had. Mocked api modules have none. */
async function renewSession(): Promise<'renewed' | 'signed-out' | 'unreachable'> {
  try {
    return (await apiModule.refreshSession()) ? 'renewed' : 'signed-out';
  } catch {
    return 'unreachable';
  }
}

/** One request to the server, and the store written if it brought something new. */
async function fetchBundle(editionId: string, stored: StoredBundle | undefined): Promise<RefreshResult> {
  // The setup wizard's phone preview opens a rave its admin does not attend (`?preview=1`;
  // the API honours it only for that rave's staff — everyone else attends first, 2026-10-15).
  // Signed out, the data pass trusts this reader more (step 99) — waited for briefly, never needed.
  const pass = Object.keys(bearer()).length ? {} : await passHeaderSoon();
  const ask = () => fetch(`${apiUrl()}/editions/${editionId}/bundle${isFramed() ? '?preview=1' : ''}`, {
    headers: { ...bearer(), ...pass, ...(stored ? { 'if-none-match': `"${stored.version}"` } : {}) },
    credentials: 'include',
  });
  let res = await ask();
  noteServerReached();
  // Not through `api`, so the 401 retry is here: an access token that expired
  // while the tab was open is a refresh away, not a sign-in.
  if (res.status === 401) {
    const renewed = await renewSession();
    if (renewed === 'renewed') res = await ask();
    // The refresh could not reach the server: nothing is known about the
    // session, so this is a network failure, not a refusal.
    else if (renewed === 'unreachable') throw new Error('bundle: the session could not be renewed');
  }
  if (res.status === 304 && stored) {
    const fetchedAt = Date.now();
    // The copy is current as of now — that is what "last synced" should say.
    await db.bundles.update(editionId, { fetchedAt }).catch(() => {});
    return { bundle: stored.bundle, source: 'unchanged', fetchedAt };
  }
  if (!res.ok) throw await refusal(res);

  const bundle = (await res.json()) as EditionBundle;
  const fetchedAt = Date.now();
  // One write, one transaction: the stored bundle is never partly new. Signed out, none: online only.
  const who = owner();
  if (who && !overForOffline(bundle.edition?.endsAt)) await db.bundles.put({ editionId, version: bundle.version, bundle, fetchedAt, owner: who });
  return { bundle, source: 'fresh', fetchedAt };
}

/**
 * A public page's preview of the rave app (`lib/preview.ts`): the open
 * `GET /public/rave/bundle` on the rave's own host — the bundle's own shape,
 * trimmed to one sample day and the live window. Nothing is stored: a preview
 * is a picture, not this device's copy of the rave.
 */
export async function fetchPreviewBundle(editionSlug: string | null): Promise<RefreshResult> {
  const q = new URLSearchParams();
  if (editionSlug) q.set('edition', editionSlug);
  // The artist the page asked to preview, passed through (`PREVIEW_ARTIST`).
  const artist = typeof window === 'undefined' ? null : new URLSearchParams(window.location.search).get('artist');
  if (artist) q.set('artist', artist);
  const qs = q.toString();
  const res = await fetch(`${apiUrl()}/public/rave/bundle${qs ? `?${qs}` : ''}`, { credentials: 'include' });
  if (!res.ok) throw await refusal(res);
  const bundle = (await res.json()) as EditionBundle;
  return { bundle, source: 'fresh', fetchedAt: Date.now() };
}

export interface RefreshOptions {
  /**
   * The background revalidation was REFUSED (401/402/403/404) after the
   * stored copy was already served. The caller should load again — the next
   * load asks the network first, so the refusal becomes its error and the
   * paywall or the sign-in shows. A 402 also drops the stored copy: a paid
   * rave this account has no access to is not one it can keep reading
   * offline.
   */
  onRefused?: (err: unknown) => void;
  /**
   * Told what the background revalidation found, when it lands: a newer
   * bundle ('fresh'), the stored one confirmed ('unchanged'), or no answer in
   * 2.5s / a failure ('offline' — and later 'fresh' if the slow request does
   * eventually arrive).
   */
  onUpdate?: (result: RefreshResult) => void;
}

/** A refusal is remembered, and a 402 takes the stored copy with it. */
async function noteRefusal(editionId: string, err: unknown): Promise<void> {
  refused.add(editionId);
  if ((err as { status?: number }).status === 402) await db.bundles.delete(editionId).catch(() => {});
}

/*
 * The bundle, asked for as soon as there is a session (step 14, 2026-10-05).
 *
 * The rave's screen sits behind the account-completion check, so the bundle
 * used to wait for `/me/completion` to answer and only then start: refresh →
 * completion → bundle → paint, three round trips in a row, and the largest
 * paint at 5 s. `WarmEdition` (`app/App.tsx`) starts it beside the check;
 * `refreshBundle` takes that answer if it SUCCEEDED and otherwise asks again
 * exactly as before — so a refusal (an unfinished account, a paywall) is only
 * ever recorded by the real request, never by the early one.
 */
const WARM_FOR_MS = 15_000;
const warm = new Map<string, { at: number; result: Promise<RefreshResult | null> }>();

export function warmBundle(editionId: string): void {
  if (warm.has(editionId)) return;
  const result = readStored(editionId)
    .then((stored) => fetchBundle(editionId, ownStored(stored)))
    .catch(() => null);
  warm.set(editionId, { at: Date.now(), result });
}

/** A warm answer still fresh enough to use — taken once. */
function takeWarm(editionId: string): Promise<RefreshResult | null> | null {
  const w = warm.get(editionId);
  warm.delete(editionId);
  return w && Date.now() - w.at < WARM_FOR_MS ? w.result : null;
}

export async function refreshBundle(editionId: string, opts: RefreshOptions = {}): Promise<RefreshResult> {
  const stored = ownStored(await readStored(editionId));
  const early = takeWarm(editionId);
  const network = (early
    ? early.then((r) => r ?? fetchBundle(editionId, stored))
    : fetchBundle(editionId, stored)).then(
    (result) => { refused.delete(editionId); return result; },
    async (err: unknown) => { if (isRefusal(err)) await noteRefusal(editionId, err); throw err; },
  );

  if (stored) {
    const offline: RefreshResult = { bundle: stored.bundle, source: 'offline', fetchedAt: stored.fetchedAt };
    const { onUpdate } = opts;

    // Nobody to tell later — or the last answer was a refusal, which must
    // reach the screen — so race: the network if it answers in 2.5s, else
    // the stored copy. The request keeps going and still stores.
    if (!onUpdate || refused.has(editionId)) {
      try {
        return await withTimeout(network);
      } catch (err) {
        network.catch(() => {});
        if (isRefusal(err)) throw err;
        return offline;
      }
    }

    // Told on a LATER task, never inside this call: a result handed to the
    // query cache before this function's own would be overwritten by it.
    const tell = (result: RefreshResult) => { setTimeout(() => onUpdate(result), 0); };
    let settled = false;
    const timer = setTimeout(() => { if (!settled) onUpdate(offline); }, NETWORK_TIMEOUT_MS);
    network.then(
      (result) => { settled = true; clearTimeout(timer); tell(result); },
      (err: unknown) => {
        settled = true;
        clearTimeout(timer);
        if (isRefusal(err)) setTimeout(() => opts.onRefused?.(err), 0);
        else tell(offline);
      },
    );
    return { bundle: stored.bundle, source: 'stored', fetchedAt: stored.fetchedAt };
  }

  try {
    return await withTimeout(network, FIRST_LOAD_TIMEOUT_MS);
  } catch (err) {
    network.catch(() => {});
    // The server ANSWERED (401, 402, 500): that is its own screen.
    if (isApiError(err)) throw err;
    // No answer at all: no signal, wifi with no upstream, or a hang.
    throw new NoStoredCopyError(err);
  }
}

/* ------------------------------------------------------------ the assets */

/** Asset caches are per EDITION and per bundle version: `myrave-assets|<editionId>|<version>`. */
const ASSET_PREFIX = 'myrave-assets|';

export function assetCacheName(editionId: string, version: string): string {
  return `${ASSET_PREFIX}${editionId}|${version}`;
}

/** The edition a cache name belongs to, or null for a cache that is not an asset cache. */
function editionOfCache(name: string): string | null {
  if (!name.startsWith(ASSET_PREFIX)) return null;
  const last = name.lastIndexOf('|');
  return last > ASSET_PREFIX.length - 1 ? name.slice(ASSET_PREFIX.length, last) : null;
}

/**
 * What a download fetches: photos, the map, logos — everything the bundle
 * references except audio. Audio previews are cached on first PLAY by the
 * service worker (the pilot's rule): downloading every preview up front would
 * multiply the download for something most people never press.
 */
export function downloadable(bundle: EditionBundle) {
  return bundle.assets.filter((a) => !a.mimeType.startsWith('audio/'));
}

/**
 * Fetch one asset in a form the cache can keep.
 *
 * `mode: 'cors'` first: the media host sends `Access-Control-Allow-Origin: *`
 * (status.md step 49), so the answer is a real response with a status. A
 * plain fetch of a cross-origin image from a page is fine too, but the one an
 * `<img>` makes is OPAQUE (status 0, `ok` false) — and a cache that only keeps
 * `ok` responses silently kept none of them.
 *
 * If CORS is refused (a host without the header), fall back to an opaque
 * response and keep it deliberately — for images only, where the browser can
 * still draw it. An opaque entry costs more quota than its bytes, which is the
 * price of a photo that works offline at all.
 */
export async function fetchCacheable(url: string, isImage: boolean, signal?: AbortSignal): Promise<Response | null> {
  try {
    const res = await fetch(url, { mode: 'cors', credentials: 'omit', signal });
    return res.status === 200 ? res : null;
  } catch (err) {
    if (signal?.aborted || !isImage) throw err;
    const res = await fetch(url, { mode: 'no-cors', credentials: 'omit', signal });
    return res.type === 'opaque' || res.status === 200 ? res : null;
  }
}

/**
 * Precache every asset the edition references.
 *
 * The list comes from the bundle's own asset manifest, which the server
 * derives from the asset graph. It is never hand-written — the pilot
 * enumerates ~230 artist photo paths in its service worker, so adding a photo
 * without adding its path means it silently never reaches an offline user.
 */
export interface PrecacheOutcome {
  /** Assets now in this version's cache. */
  done: number;
  total: number;
  /** The bytes of those assets — successes only. */
  bytes: number;
  /** Every asset is cached; `finishedAt` was written. */
  complete: boolean;
  /**
   * Stopped because the link is ON but useless: files in a row did not arrive
   * by their deadline. The rest is fetched on a later try.
   */
  weak?: boolean;
}

/**
 * **A file that has not arrived by its deadline is left for later** (the
 * user, 2026-10-14: a festival signal is often on and useless — "the app
 * thinks there is internet access then everything fails"). The deadline is
 * what the file would take at `SLOW_LINK_BYTES_PER_S`, never under
 * `ASSET_MIN_TIMEOUT_MS`: a photo gets 20 s, the 3 MB site map ~3 minutes.
 */
export const ASSET_MIN_TIMEOUT_MS = 20_000;
const SLOW_LINK_BYTES_PER_S = 16_000;
export const assetDeadlineMs = (bytes: number) =>
  Math.max(ASSET_MIN_TIMEOUT_MS, Math.ceil((bytes / SLOW_LINK_BYTES_PER_S) * 1000));
/**
 * This many files in a row past their deadline and the link is too weak to
 * carry a download: it stops, and leaves what signal there is to the app.
 */
export const WEAK_AFTER = 3;

export async function precacheAssets(
  bundle: EditionBundle,
  onProgress?: (done: number, total: number, bytes: number) => void,
  signal?: AbortSignal,
): Promise<PrecacheOutcome> {
  if (typeof caches === 'undefined') return { done: 0, total: 0, bytes: 0, complete: false };
  // Signed out is online only (2026-10-27): no photos or map kept for offline.
  if (!signedInHere()) return { done: 0, total: 0, bytes: 0, complete: false };

  const editionId = bundle.edition.id;
  // Ends every file in flight at once: the caller's abort, or a weak link.
  const all = new AbortController();
  signal?.addEventListener('abort', () => all.abort(), { once: true });
  if (signal?.aborted) all.abort();
  let lateInARow = 0;
  let weak = false;
  const cacheName = assetCacheName(editionId, bundle.version);
  const cache = await caches.open(cacheName);
  const assets = downloadable(bundle);
  const total = assets.length;
  let done = 0;
  let bytes = 0;

  // A small pool: enough to use the connection, few enough not to drown a
  // weak one. The pilot learned this the hard way on festival wifi.
  const CONCURRENCY = 6;
  const queue = [...assets];

  async function worker(): Promise<void> {
    while (queue.length > 0) {
      if (all.signal.aborted) return;
      const asset = queue.shift();
      if (!asset) return;
      let ok = false;
      let late = false;
      const one = new AbortController();
      const stop = () => one.abort();
      all.signal.addEventListener('abort', stop, { once: true });
      const deadline = setTimeout(() => { late = true; one.abort(); }, assetDeadlineMs(asset.bytes));
      try {
        // Already stored — by an earlier version's cache or the worker —
        // counts as done: the url is content-addressed.
        const have = (await cache.match(asset.url)) ?? (await caches.match(asset.url));
        if (have) {
          if (!(await cache.match(asset.url))) await cache.put(asset.url, have.clone());
          ok = true;
        } else {
          const res = await fetchCacheable(asset.url, asset.mimeType.startsWith('image/'), one.signal);
          if (res) {
            // The body arrives inside the deadline too: `put` reads it.
            await cache.put(asset.url, res);
            ok = true;
          }
        }
      } catch {
        // One missing photo must not fail the download. The lineup matters
        // more than any single image — and it is fetched again next time.
      } finally {
        clearTimeout(deadline);
        all.signal.removeEventListener('abort', stop);
      }
      if (ok) lateInARow = 0;
      else if (late && !all.signal.aborted) {
        lateInARow++;
        if (lateInARow >= WEAK_AFTER) { weak = true; all.abort(); }
      }
      /*
       * ONLY a success counts. A failure used to count as done, so a download
       * interrupted by the signal dropping wrote `finishedAt` and was never
       * resumed: the photos it missed stayed missing for the whole festival.
       */
      if (!ok) continue;
      done++;
      bytes += asset.bytes;
      onProgress?.(done, total, bytes);
      await db.assetProgress.put({ editionId, version: bundle.version, total, done, bytes }).catch(() => {});
    }
  }

  await Promise.all(Array.from({ length: CONCURRENCY }, worker));
  const complete = done === total;
  if (signal?.aborted) return { done, total, bytes, complete: false };
  if (weak) {
    await db.assetProgress.put({ editionId, version: bundle.version, total, done, bytes }).catch(() => {});
    return { done, total, bytes, complete: false, weak: true };
  }
  // `finishedAt` only when EVERYTHING is cached. Anything less is resumed on
  // the next open, or when the network comes back (OfflineProvider).
  await db.assetProgress.put({
    editionId, version: bundle.version, total, done, bytes, ...(complete ? { finishedAt: Date.now() } : {}),
  }).catch(() => {});
  // An older version's cache may still hold what this one missed; it goes
  // only once this one is whole.
  if (!complete) return { done, total, bytes, complete };

  // Drop THIS edition's older versions — the content hash means a new
  // version is a new cache, and the old one is dead weight. Another edition's
  // caches are left alone: attending 2027 must not wipe 2026's photos. The
  // pre-2026-09-28 names (`myrave-assets-<version>`, no edition in them) are
  // unowned and dropped; each rave refills its own on its next open.
  for (const name of await caches.keys()) {
    if (name === cacheName) continue;
    if (name.startsWith('myrave-assets-') || editionOfCache(name) === editionId) {
      await caches.delete(name);
    }
  }
  return { done, total, bytes, complete };
}

/** Has this bundle version's download already finished on this device? */
export async function downloadFinished(editionId: string, version: string): Promise<{ total: number; bytes: number } | null> {
  try {
    const p = await db.assetProgress.get(editionId);
    return p?.finishedAt && p.version === version ? { total: p.total, bytes: p.bytes } : null;
  } catch {
    return null;
  }
}

/**
 * How far this rave's offline copy has come, for the version matrix (the user, 2026-10-30, the
 * iPhone walk of 0.14.72: "a black screen with no signal — maybe it's a download issue"). Read
 * from what the PHONE holds — the stored bundle's asset list and the download's own progress row
 * — so it answers with no network. Files and sizes are the downloadable ones (`downloadable`:
 * no audio). A progress row for an older bundle version counts as nothing yet: the new version is
 * a new download (most of it already on the phone, content-addressed, so it runs fast).
 */
export interface OfflineCopyStatus {
  /** The lineup itself (the bundle) is on this phone. */
  stored: boolean;
  done: number;
  total: number;
  bytes: number;
  totalBytes: number;
  /** Every photo and the map are on this phone. */
  complete: boolean;
}

export async function offlineCopyStatus(editionId: string): Promise<OfflineCopyStatus> {
  const none: OfflineCopyStatus = { stored: false, done: 0, total: 0, bytes: 0, totalBytes: 0, complete: false };
  try {
    const kept = await db.bundles.get(editionId);
    if (!kept) return none;
    const files = Array.isArray(kept.bundle?.assets) ? downloadable(kept.bundle) : [];
    const total = files.length;
    const totalBytes = files.reduce((sum, a) => sum + (Number.isFinite(a.bytes) ? a.bytes : 0), 0);
    const p = await db.assetProgress.get(editionId).catch(() => undefined);
    const mine = p && p.version === kept.version ? p : null;
    const done = Math.min(mine?.done ?? 0, total);
    const complete = total === 0 || Boolean(mine?.finishedAt);
    return {
      stored: true,
      done: complete ? total : done,
      total,
      bytes: complete ? totalBytes : Math.min(mine?.bytes ?? 0, totalBytes),
      totalBytes,
      complete,
    };
  } catch {
    return none;
  }
}
